/**
 * 远期房态：前端直调 PMS getAliasPriceQuantity + 房态「某日平均价」getAvgPriceByDate 取年同比/周环比 + 本地缓存（内存 2 分钟 + localStorage）
 * 直调失败时（如部署环境 CORS）回退到 GET /api/store-revenue/component?componentType=room-status-recently，保证价格/K 线等下半部分有数据
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { getAliasPriceQuantityFromThirdParty, getAvgPricesByDates, getStoreRevenueComponent, } from '../../service/storeRevenueApi';
import { aliasPriceQuantityToBaseChartData } from '../components/RoomStatusRecently/utils/roomStatusRecentlyChartTransformer';
import { getStoreFromLocalStorage, setStoreSliceInLocalStorage } from './storeRevenueLocalStorage';
/** Unix 秒 → YYYY-MM-DD */
function dateSecToYYYYMMDD(dateSec) {
    var d = new Date(dateSec * 1000);
    var y = d.getFullYear();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return "".concat(y, "-").concat(m, "-").concat(day);
}
/** 某日去年同日、以及七天前的日期（用于年同比/周环比折线） */
function toLastYearAndSevenDaysAgo(dateStr) {
    var d = new Date(dateStr + 'T12:00:00');
    var lastYear = new Date(d);
    lastYear.setFullYear(d.getFullYear() - 1);
    var seven = new Date(d);
    seven.setDate(d.getDate() - 7);
    var fmt = function (x) {
        return "".concat(x.getFullYear(), "-").concat(String(x.getMonth() + 1).padStart(2, '0'), "-").concat(String(x.getDate()).padStart(2, '0'));
    };
    return { lastYear: fmt(lastYear), sevenDaysAgo: fmt(seven) };
}
var TTL_MS = 2 * 60 * 1000; // 2 分钟
var cache = new Map();
var inFlight = new Map();
function cacheKey(hotelId) {
    return "roomStatusRecently:".concat(String(hotelId));
}
function isValid(entry) {
    return Date.now() - entry.fetchedAt < TTL_MS;
}
function hasBaseChartDataShape(v) {
    if (v == null || typeof v !== 'object')
        return false;
    var o = v;
    return 'categories' in o && 'dataset' in o;
}
/** 从 localStorage 恢复（若有且在 TTL 内）并写入内存缓存，供 hook 做初始渲染 */
export function getRoomStatusRecentlyFromLocalStorageSync(hotelId) {
    var _a, _b;
    var stored = (_a = getStoreFromLocalStorage(hotelId)) === null || _a === void 0 ? void 0 : _a.roomStatusRecently;
    if (!(stored === null || stored === void 0 ? void 0 : stored.data) || typeof stored.fetchedAt !== 'number')
        return null;
    if (Date.now() - stored.fetchedAt > TTL_MS)
        return null;
    var inner = (_b = stored.data) === null || _b === void 0 ? void 0 : _b.baseChartData;
    if (!hasBaseChartDataShape(inner))
        return null;
    cache.set(cacheKey(hotelId), { data: inner, fetchedAt: stored.fetchedAt });
    return inner;
}
/**
 * 获取远期房态 baseChartData：优先内存缓存，其次 localStorage（同 TTL），再直调 getAliasPriceQuantity 并写缓存与 localStorage
 */
export function getRoomStatusRecentlyWithCache(hotelId) {
    return __awaiter(this, void 0, Promise, function () {
        var key, entry, fromLocal, existing, promise;
        var _this = this;
        return __generator(this, function (_a) {
            key = cacheKey(hotelId);
            entry = cache.get(key);
            if (entry && isValid(entry)) {
                return [2 /*return*/, entry.data];
            }
            fromLocal = getRoomStatusRecentlyFromLocalStorageSync(hotelId);
            if (fromLocal != null)
                return [2 /*return*/, fromLocal];
            existing = inFlight.get(key);
            if (existing)
                return [2 /*return*/, existing];
            promise = (function () { return __awaiter(_this, void 0, Promise, function () {
                var response, baseChartData, dates, lastYearDates, sevenDaysAgoDates, _a, yearOverYearPrices, weekOverWeekPrices, comp, _b, fetchedAt, directErr_1, slice, baseChartData, fetchedAt, fallbackErr_1;
                var _c, _d, _e;
                return __generator(this, function (_f) {
                    switch (_f.label) {
                        case 0:
                            _f.trys.push([0, 6, , 11]);
                            return [4 /*yield*/, getAliasPriceQuantityFromThirdParty(hotelId)];
                        case 1:
                            response = _f.sent();
                            baseChartData = aliasPriceQuantityToBaseChartData(response);
                            dates = ((_c = response.data) !== null && _c !== void 0 ? _c : []).map(function (d) { return dateSecToYYYYMMDD(d.date); });
                            if (!(dates.length > 0)) return [3 /*break*/, 5];
                            lastYearDates = dates.map(function (dateStr) { return toLastYearAndSevenDaysAgo(dateStr).lastYear; });
                            sevenDaysAgoDates = dates.map(function (dateStr) { return toLastYearAndSevenDaysAgo(dateStr).sevenDaysAgo; });
                            _f.label = 2;
                        case 2:
                            _f.trys.push([2, 4, , 5]);
                            return [4 /*yield*/, Promise.all([
                                    getAvgPricesByDates(hotelId, lastYearDates),
                                    getAvgPricesByDates(hotelId, sevenDaysAgoDates),
                                ])];
                        case 3:
                            _a = _f.sent(), yearOverYearPrices = _a[0], weekOverWeekPrices = _a[1];
                            baseChartData.dataset.yearOverYear = yearOverYearPrices;
                            baseChartData.dataset.weekOverWeek = weekOverWeekPrices;
                            comp = baseChartData.comparison;
                            if (comp) {
                                comp.yearOverYear = __assign(__assign({}, (comp.yearOverYear && typeof comp.yearOverYear === 'object' && !Array.isArray(comp.yearOverYear) ? comp.yearOverYear : {})), { value: yearOverYearPrices });
                                comp.weekOverWeek = __assign(__assign({}, (comp.weekOverWeek && typeof comp.weekOverWeek === 'object' && !Array.isArray(comp.weekOverWeek) ? comp.weekOverWeek : {})), { value: weekOverWeekPrices });
                            }
                            return [3 /*break*/, 5];
                        case 4:
                            _b = _f.sent();
                            return [3 /*break*/, 5];
                        case 5:
                            fetchedAt = Date.now();
                            cache.set(key, { data: baseChartData, fetchedAt: fetchedAt });
                            setStoreSliceInLocalStorage(hotelId, 'roomStatusRecently', {
                                data: { baseChartData: baseChartData },
                                fetchedAt: fetchedAt,
                            });
                            return [2 /*return*/, baseChartData];
                        case 6:
                            directErr_1 = _f.sent();
                            _f.label = 7;
                        case 7:
                            _f.trys.push([7, 9, , 10]);
                            return [4 /*yield*/, getStoreRevenueComponent(hotelId, 'room-status-recently')];
                        case 8:
                            slice = _f.sent();
                            baseChartData = (_d = slice === null || slice === void 0 ? void 0 : slice.roomStatusRecently) === null || _d === void 0 ? void 0 : _d.baseChartData;
                            if (baseChartData && hasBaseChartDataShape(baseChartData)) {
                                fetchedAt = Date.now();
                                cache.set(key, { data: baseChartData, fetchedAt: fetchedAt });
                                setStoreSliceInLocalStorage(hotelId, 'roomStatusRecently', {
                                    data: { baseChartData: baseChartData },
                                    fetchedAt: fetchedAt,
                                });
                                return [2 /*return*/, baseChartData];
                            }
                            return [3 /*break*/, 10];
                        case 9:
                            fallbackErr_1 = _f.sent();
                            if (typeof process !== 'undefined' && ((_e = process.env) === null || _e === void 0 ? void 0 : _e.NODE_ENV) === 'development') {
                                // eslint-disable-next-line no-console
                                console.warn('[远期房态] 直调与后端回退均失败', { hotelId: hotelId, direct: directErr_1, fallback: fallbackErr_1 });
                            }
                            return [3 /*break*/, 10];
                        case 10: throw directErr_1;
                        case 11: return [2 /*return*/];
                    }
                });
            }); })().finally(function () {
                inFlight.delete(key);
            });
            inFlight.set(key, promise);
            return [2 /*return*/, promise];
        });
    });
}
export function clearRoomStatusRecentlyCache(hotelId) {
    cache.delete(cacheKey(hotelId));
    inFlight.delete(cacheKey(hotelId));
}
